// src/lib/api/financial/quarterlyTax.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, createSuccessResponse } from '../shared/helpers'
import { withErrorHandling } from '../shared/errors'
import { validateRequired } from '../shared/validation'

/**
 * Get all quarterly tax payments for a given year
 */
const _getQuarterlyPayments = async (year) => {
  validateRequired(year, 'Tax year')

  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('quarterly_tax_payments')
    .select('*')
    .eq('company_id', companyId)
    .eq('tax_year', year)
    .order('quarter', { ascending: true })

  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Create or update a quarterly tax payment record
 * Uses upsert on (company_id, tax_year, quarter) unique constraint
 */
const _upsertQuarterlyPayment = async ({ tax_year, quarter, estimated_amount, paid_amount, paid_date, due_date, notes }) => {
  validateRequired(tax_year, 'Tax year')
  validateRequired(quarter, 'Quarter')
  validateRequired(due_date, 'Due date')

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  const payload = {
    company_id: companyId,
    tax_year,
    quarter,
    estimated_amount: estimated_amount ?? 0,
    paid_amount: paid_amount ?? 0,
    paid_date: paid_date || null,
    due_date,
    notes: notes || null,
    updated_at: new Date().toISOString()
  }

  const { data, error } = await supabase
    .from('quarterly_tax_payments')
    .upsert(payload, {
      onConflict: 'company_id,tax_year,quarter'
    })
    .select()
    .single()

  if (error) throw error
  return data
}

// Export wrapped functions
export const getQuarterlyPayments = withErrorHandling(_getQuarterlyPayments, 'get quarterly tax payments', 'quarterly_tax_payments')
export const upsertQuarterlyPayment = withErrorHandling(_upsertQuarterlyPayment, 'upsert quarterly tax payment', 'quarterly_tax_payments')
